import React, { useState, useEffect } from 'react';
import { Users, Activity, Eye, RefreshCw, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import api from '@/lib/api';

const StatCard = ({ icon: Icon, label, value, loading, color = 'text-red-400' }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <div className="text-3xl font-bold text-slate-100 mt-1">
            {loading ? <Skeleton className="h-9 w-16" /> : value}
          </div>
        </div>
        <div className="p-3 rounded-lg bg-[#DC2626]/10">
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ActivityPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchActivity(); }, []);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/activity');
      setData(r.data);
    } catch { toast.error('Failed to load activity data'); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">User Activity</h1>
          <p className="text-slate-300">Track user engagement and platform usage</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchActivity} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Users} label="Active Users (7d)" value={data?.active_users_7d || 0} loading={loading} />
        <StatCard icon={TrendingUp} label="Total Users" value={data?.total_users || 0} loading={loading} color="text-green-500" />
        <StatCard icon={Eye} label="Content Reads (7d)" value={data?.total_reads_7d || 0} loading={loading} color="text-purple-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Daily Active Users Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-slate-100">Daily Active Users (14 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-52 w-full" /> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data?.daily_active_users || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="users" fill="#DC2626" radius={[4, 4, 0, 0]} name="Active Users" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Action Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-slate-100">Top Actions (30 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-52 w-full" /> : (
              <div className="space-y-2 max-h-52 overflow-auto">
                {(data?.action_breakdown || []).map((a, i) => (
                  <div key={a.action} className="flex items-center justify-between p-2 rounded bg-[#0A1024]">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-gray-500 w-5">{i + 1}.</span>
                      <Badge variant="secondary" className="text-xs">{a.action}</Badge>
                    </div>
                    <span className="text-sm font-bold text-slate-100">{a.count}</span>
                  </div>
                ))}
                {(!data?.action_breakdown || data.action_breakdown.length === 0) && (
                  <p className="text-center text-gray-400 py-8">No activity data yet</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Most Active Users */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base text-slate-100">Most Active Users (30 days)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-40 w-full" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-[#0A1024]">
                    <th className="text-left p-3 font-medium text-slate-300">#</th>
                    <th className="text-left p-3 font-medium text-slate-300">User</th>
                    <th className="text-left p-3 font-medium text-slate-300">Email</th>
                    <th className="text-right p-3 font-medium text-slate-300">Actions</th>
                    <th className="text-right p-3 font-medium text-slate-300">Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.top_users || []).map((u, i) => (
                    <tr key={u.user_id} className="border-b hover:bg-[#0A1024]">
                      <td className="p-3 text-gray-400">{i + 1}</td>
                      <td className="p-3 font-medium text-slate-100">{u.full_name || '—'}</td>
                      <td className="p-3 text-slate-300">{u.email}</td>
                      <td className="p-3 text-right"><Badge className="bg-[#DC2626]/10 text-red-400">{u.action_count}</Badge></td>
                      <td className="p-3 text-right text-gray-500 text-xs">{u.last_action ? new Date(u.last_action).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                  {(!data?.top_users || data.top_users.length === 0) && (
                    <tr><td colSpan={5} className="p-8 text-center text-gray-400">No user activity yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityPage;
